import { useCallback, useRef, useState } from 'react';
import RequestType from '@enums/RequestType';
import useGetApi from './useGetApi';
import useUpdateApi from './useUpdateApi';

const useApi = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const abortController = useRef(null);
  const { getData } = useGetApi();
  const { update, bulkUpdate } = useUpdateApi();

  const handleGet = async (body) => {
    return await getData(body);
  };

  const handleUpdate = async (body) => {
    return await update(body);
  };

  // Performs the request.
  const request = async (params) => {
    // Cancel the previous fetch request if a new one is sent.
    if (abortController.current) {
      abortController.current.abort;
    }

    abortController.current = new AbortController();

    const {
      url,
      method,
      body,
      signal = abortController.current.signal,
    } = params;

    setLoading(true);
    setError(false);
    setData(null);

    try {
      // GET requests.
      if (method === RequestType.GET) {
        const result = await getData(body);
        setData(result);
        setLoading(false);
        return result;
      }

      // Bulk UPDATE requests are treated differently for now.
      if (body.action === 'bulk') {
        const result = await bulkUpdate(body);
        setData(result);
        setLoading(false);
        return result;
      }

      // Standard UPDATE requests.
      const result = await update(body);
      setData(result);
      setLoading(false);

      return result;
    } catch (error) {
      // We don't need to throw errors for aborting fetch requests.
      if (error.name === 'AbortError') {
        return;
      }

      setError(error);
      setLoading(false);
      throw error;
    }
  };

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
    if (abortController.current) {
      abortController.current.abort();
    }
  }, []);

  return {
    data,
    error,
    isLoading,
    request,
    reset,
  };
};

export default useApi;
